import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatReplicationAge,
  formatReplicationBytes,
  presentReplicationHealthStatus,
  REPLICATION_LAG_CRIT_BYTES_DEFAULT,
  REPLICATION_LAG_WARN_BYTES_DEFAULT,
  type EgressClusterMemberEligibility,
  type EgressLinkListEntry,
  type EgressLinkStatus,
  type EgressPathSnapshotRow,
  type ReplicationHealthSample,
  type ReplicationHealthStatus,
  type ReplicationStoredHealthStatus,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useEgressLinks,
  useEgressNodeTraffic,
  useEgressSnapshots,
  useEgressSwitchEvents,
  usePatchEgressLinkDispatch,
  usePatchEgressLinkStatus,
  useReplicationHealth,
} from '../api/useEgressLines.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT, type I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  InfoHint,
  PageHeader,
  Panel,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';
import { ExitShareBar } from './egress-lines/ExitShareBar.tsx';
import { NodeIdentity } from './egress-lines/NodeIdentity.tsx';
import { TargetDecisionTree } from './egress-lines/TargetDecisionTree.tsx';
import { UpstreamLabel } from './egress-lines/UpstreamLabel.tsx';
import { buildDecisionForest } from './egress-lines/decisions.ts';
import {
  NODE_ACTIVE_AFTER_MS,
  buildMatrix,
  destRegionsFor,
  partitionMatrixNodes,
  type LineBadge,
  type MatrixNode,
} from './egress-lines/matrix.ts';
import { EgressClusterTab } from './egress-lines/EgressClusterTab.tsx';
import { EgressPolicyTab } from './egress-lines/EgressPolicyTab.tsx';

const PAGE_TABS = ['matrix', 'policies', 'clusters'] as const;
type PageTab = (typeof PAGE_TABS)[number];

function tabFromSearch(value: string | null): PageTab {
  if (value === 'policies' || value === 'clusters' || value === 'matrix') return value;
  return 'matrix';
}

const REPLICATION_STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * `/supply/egress-lines` — multiregion line matrix (#1967 R5 / #1979).
 *
 * Snapshot numbers are the node's self-report. Selection stays locally
 * authoritative on each gateway; a stale view is marked, never treated
 * as the live path.
 */

export function EgressLinesPage(): JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromSearch(searchParams.get('tab'));
  const [previewMembers, setPreviewMembers] = useState<readonly EgressClusterMemberEligibility[]>([]);
  const linksQ = useEgressLinks();
  const snapshotsQ = useEgressSnapshots();
  const eventsQ = useEgressSwitchEvents();
  const servingQ = useEgressNodeTraffic();
  const replicationQ = useReplicationHealth();
  const links = linksQ.data ?? [];
  const snapshots = snapshotsQ.data ?? [];
  const events = eventsQ.data ?? [];
  const serving = servingQ.data ?? [];
  const replication = replicationQ.data ?? [];
  const dests = destRegionsFor(snapshots, links, events, serving);
  const nodes = buildMatrix(snapshots, events, Date.now(), serving, links);
  const split = partitionMatrixNodes(nodes);
  const servingMinutes = String(Math.round(NODE_ACTIVE_AFTER_MS / 60_000));
  const forest = buildDecisionForest(split.active, snapshots);
  const ingressRegions = useMemo(
    () => [...new Set(nodes.map((node) => node.gatewayRegion).filter((region) => region.length > 0))].sort((a, b) => a.localeCompare(b)),
    [nodes],
  );

  return (
    <section className="flex flex-col gap-6" data-testid="egress-lines-page">
      <PageHeader title={t('egress_lines.title')} sub={t('egress_lines.subtitle')} />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          const next = tabFromSearch(value);
          const params = new URLSearchParams(searchParams);
          if (next === 'matrix') params.delete('tab');
          else params.set('tab', next);
          setSearchParams(params, { replace: true });
        }}
      >
        <TabsList ariaLabel={t('egress_lines.tabs_label')}>
          <TabsTrigger value="matrix" testId="egress-lines-tab-matrix">{t('egress_lines.tab.matrix')}</TabsTrigger>
          <TabsTrigger value="policies" testId="egress-lines-tab-policies">{t('egress_lines.tab.policies')}</TabsTrigger>
          <TabsTrigger value="clusters" testId="egress-lines-tab-clusters">{t('egress_lines.tab.clusters')}</TabsTrigger>
        </TabsList>
        <TabsContent value="matrix" className="mt-4 flex flex-col gap-6">
      <p className="m-0 text-dense text-paper-mid" data-testid="egress-lines-authority-note">
        {t('egress_lines.authority_note')}
      </p>
      <p className="m-0 text-dense text-paper-low" data-testid="egress-lines-matrix-empty-cell-hint">
        {t('egress_lines.matrix.empty_cell_hint')}
      </p>
      <p className="m-0 text-dense text-paper-low" data-testid="egress-lines-matrix-presence-note">
        {t('egress_lines.matrix.presence_note', { minutes: servingMinutes })}
      </p>

      <Panel testId="egress-lines-matrix" caption={t('egress_lines.matrix.title')} padded>
        {servingQ.isError ? (
          <p className="m-0 text-body text-bad" role="alert" data-testid="egress-lines-nodes-error">
            {coerceErrorMessage(servingQ.error)}
          </p>
        ) : null}
        {nodes.length === 0 && !servingQ.isError ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.matrix.empty')}</p>
        ) : nodes.length === 0 ? null : (
          <div className="flex flex-col gap-4">
            {split.active.length === 0 ? (
              <p className="m-0 text-dense text-paper-low">{t('egress_lines.matrix.empty_serving')}</p>
            ) : (
              <MatrixTable nodes={split.active} dests={dests} />
            )}
            {split.historical.length > 0 ? (
              <details data-testid="egress-lines-historical">
                <summary className="cursor-pointer text-dense text-paper-mid">
                  {t('egress_lines.matrix.historical_group', { count: String(split.historical.length) })}
                </summary>
                <div className="mt-3">
                  <MatrixTable nodes={split.historical} dests={dests} />
                </div>
              </details>
            ) : null}
          </div>
        )}
      </Panel>

      <Panel testId="egress-lines-replication" caption={t('egress_lines.replication.title')} padded>
        {replication.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.replication.empty')}</p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th>{t('egress_lines.replication.link')}</Th>
                <Th>{t('egress_lines.replication.direction')}</Th>
                <Th>{t('egress_lines.replication.status')}</Th>
                <Th>
                  <span className="inline-flex items-center gap-1">
                    {t('egress_lines.replication.lag')}
                    <InfoHint
                      label={t('egress_lines.replication.lag_hint_label')}
                      content={t('egress_lines.replication.lag_hint')}
                      testId="egress-lines-replication-lag-hint"
                    />
                  </span>
                </Th>
                <Th>
                  <span className="inline-flex items-center gap-1">
                    {t('egress_lines.replication.delay')}
                    <InfoHint
                      label={t('egress_lines.replication.delay_hint_label')}
                      content={t('egress_lines.replication.delay_hint')}
                      testId="egress-lines-replication-delay-hint"
                    />
                  </span>
                </Th>
                <Th>{t('egress_lines.replication.drift')}</Th>
                <Th>{t('egress_lines.replication.sampled')}</Th>
              </Tr>
            </THead>
            <TBody>
              {replication.map((sample) => (
                <ReplicationRow key={sample.link_name} sample={sample} />
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Panel testId="egress-lines-traffic" caption={t('egress_lines.traffic.title')} padded>
        <p className="m-0 mb-3 text-dense text-paper-low">{t('egress_lines.traffic.hint')}</p>
        <TargetDecisionTree trees={forest} />
        {trafficRows(snapshots).length === 0 ? null : (
          <div className="mt-4">
            <p className="m-0 mb-2 text-micro text-paper-low">{t('egress_lines.traffic.table_caption')}</p>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('egress_lines.matrix.node')}</Th>
                  <Th>{t('egress_lines.node.path')}</Th>
                  <Th>{t('egress_lines.traffic.upstream')}</Th>
                  <Th>{t('egress_lines.traffic.ttfb')}</Th>
                  <Th>{t('egress_lines.node.sort_key')}</Th>
                  <Th>{t('egress_lines.node.samples')}</Th>
                  <Th>{t('egress_lines.traffic.last_explore')}</Th>
                  <Th>{t('egress_lines.node.live')}</Th>
                </Tr>
              </THead>
              <TBody>
                {trafficRows(snapshots).map((row) => (
                  <Tr
                    key={`${row.nodeId}:${row.pathId}:${row.upstreamId}`}
                    data-testid={`egress-lines-traffic-${row.nodeId}-${row.pathId}-${row.upstreamId}`}
                  >
                    <Td>
                      <NodeIdentity nodeId={row.nodeId} region={row.gatewayRegion} />
                    </Td>
                    <Td className="font-mono text-dense">{row.pathId}</Td>
                    <Td>
                      <UpstreamLabel
                        upstreamId={row.upstreamId}
                        name={row.upstreamName}
                        region={row.upstreamRegion}
                      />
                    </Td>
                    <Td num>{row.p50TtfbMs === null ? '—' : `${Math.round(row.p50TtfbMs)}ms`}</Td>
                    <Td num>{row.sortKeyMs === null ? '—' : `${Math.round(row.sortKeyMs)}ms`}</Td>
                    <Td num>{row.sampleCount}</Td>
                    <Td className="font-mono text-micro text-paper-low">{row.lastExploredAt ?? '—'}</Td>
                    <Td>
                      {row.selected ? (
                        <Chip tone="ok" lamp={false}>{t('egress_lines.node.live_chip')}</Chip>
                      ) : '—'}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel testId="egress-lines-links" caption={t('egress_lines.links.title')} padded>
        {links.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.links.empty')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-dense text-paper-low" data-testid="egress-lines-dispatch-lag-note">
              {t('egress_lines.links.dispatch.lag_note')}
            </p>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('egress_lines.links.id')}</Th>
                  <Th>{t('egress_lines.links.region')}</Th>
                  <Th>{t('egress_lines.links.endpoint')}</Th>
                  <Th>{t('egress_lines.links.status')}</Th>
                  <Th>
                    <span className="inline-flex items-center gap-1">
                      {t('egress_lines.links.dispatch')}
                      <InfoHint
                        label={t('egress_lines.links.dispatch.hint_label')}
                        content={t('egress_lines.links.dispatch.hint')}
                        testId="egress-lines-dispatch-hint"
                      />
                    </span>
                  </Th>
                  <Th>{t('egress_lines.links.heartbeat')}</Th>
                  <Th>{t('egress_lines.links.note')}</Th>
                  <Th>{t('egress_lines.links.toggle')}</Th>
                </Tr>
              </THead>
              <TBody>
                {links.map((link) => (
                  <LinkRow key={link.egress_id} link={link} />
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Panel>
        </TabsContent>
        <TabsContent value="policies" className="mt-4">
          <EgressPolicyTab
            links={links}
            ingressRegions={ingressRegions}
            onPreviewMembers={setPreviewMembers}
          />
        </TabsContent>
        <TabsContent value="clusters" className="mt-4">
          <EgressClusterTab links={links} members={previewMembers} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function MatrixTable({
  nodes,
  dests,
}: {
  readonly nodes: readonly MatrixNode[];
  readonly dests: readonly string[];
}): JSX.Element {
  const t = useT();
  return (
    <Table>
      <THead>
        <Tr>
          <Th>{t('egress_lines.matrix.node')}</Th>
          <Th>{t('egress_lines.matrix.freshness')}</Th>
          <Th>{t('egress_lines.matrix.traffic_1h')}</Th>
          <Th>{t('egress_lines.matrix.traffic_24h')}</Th>
          <Th>{t('egress_lines.matrix.exit')}</Th>
          {dests.map((dest) => (
            <Th key={dest}>{dest}</Th>
          ))}
        </Tr>
      </THead>
      <TBody>
        {nodes.map((node) => (
          <MatrixRow key={node.nodeId} node={node} dests={dests} />
        ))}
      </TBody>
    </Table>
  );
}

function MatrixRow({ node, dests }: { readonly node: MatrixNode; readonly dests: readonly string[] }): JSX.Element {
  const t = useT();
  const staleClass = node.stale ? 'text-paper-low opacity-60' : 'text-paper';
  return (
    <Tr data-testid={`egress-lines-node-${node.nodeId}`}>
      <Td>
        <NodeIdentity
          nodeId={node.nodeId}
          region={node.gatewayRegion}
          to={`/supply/egress-lines/${encodeURIComponent(node.nodeId)}`}
          testId={`egress-lines-node-link-${node.nodeId}`}
        />
      </Td>
      <Td className={staleClass} data-testid={`egress-lines-freshness-${node.nodeId}`}>
        {node.ageSeconds === null
          ? t('egress_lines.matrix.freshness_unknown')
          : t('egress_lines.matrix.freshness_ago', { seconds: String(node.ageSeconds) })}
        {node.stale ? (
          <Chip tone="neutral" lamp={false} className="ml-2" testId={`egress-lines-stale-${node.nodeId}`}>
            {t('egress_lines.matrix.stale')}
          </Chip>
        ) : null}
      </Td>
      <Td className="font-mono text-micro" data-testid={`egress-lines-traffic-1h-${node.nodeId}`}>
        <TrafficCell
          requests={node.traffic?.requests_1h ?? 0}
          tokens={node.traffic?.tokens_1h ?? 0}
        />
      </Td>
      <Td className="font-mono text-micro" data-testid={`egress-lines-traffic-24h-${node.nodeId}`}>
        <TrafficCell
          requests={node.traffic?.requests_24h ?? 0}
          tokens={node.traffic?.tokens_24h ?? 0}
        />
      </Td>
      <Td>
        <ExitShareBar
          nodeId={node.nodeId}
          shares={node.traffic?.exit_share_24h ?? []}
        />
      </Td>
      {dests.map((dest) => {
        const cell = node.cells[dest];
        if (cell === undefined) {
          return <Td key={dest} className="font-mono text-micro text-paper-low">—</Td>;
        }
        return (
          <Td key={dest} className={node.stale ? 'opacity-60' : undefined}>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-dense text-paper">{cell.pathId}</span>
              {cell.sortKeyMs !== null ? (
                <span className="font-mono text-micro text-paper-low">{Math.round(cell.sortKeyMs)}ms</span>
              ) : null}
              <Chip tone={badgeTone(cell.badge)} lamp={false}>
                {badgeLabel(t, cell.badge)}
              </Chip>
            </div>
          </Td>
        );
      })}
    </Tr>
  );
}

function trafficRows(snapshots: readonly EgressPathSnapshotRow[]): readonly {
  readonly nodeId: string;
  readonly gatewayRegion: string;
  readonly pathId: string;
  readonly upstreamId: string;
  readonly upstreamName: string | null;
  readonly upstreamRegion: string | null;
  readonly p50TtfbMs: number | null;
  readonly sortKeyMs: number | null;
  readonly sampleCount: number;
  readonly lastExploredAt: string | null;
  readonly selected: boolean;
}[] {
  const rows = [];
  for (const snapshot of snapshots) {
    for (const target of snapshot.target_observations ?? []) {
      rows.push({
        nodeId: snapshot.node_id,
        gatewayRegion: snapshot.gateway_region,
        pathId: snapshot.path_id,
        upstreamId: target.upstream_id,
        upstreamName: target.upstream_name ?? null,
        upstreamRegion: target.upstream_region ?? null,
        p50TtfbMs: target.p50_ttfb_ms,
        sortKeyMs: target.p75_sort_key_ms,
        sampleCount: target.sample_count,
        lastExploredAt: target.last_explored_at,
        selected: target.selected,
      });
    }
  }
  return rows;
}

function badgeLabel(t: (key: I18nKey) => string, badge: LineBadge): string {
  if (badge === 'excellent') return t('egress_lines.badge.excellent');
  if (badge === 'good') return t('egress_lines.badge.good');
  if (badge === 'degraded') return t('egress_lines.badge.degraded');
  if (badge === 'breaker') return t('egress_lines.badge.breaker');
  if (badge === 'same_region') return t('egress_lines.badge.same_region');
  if (badge === 'no_traffic') return t('egress_lines.badge.no_traffic');
  if (badge === 'stale_unknown') return t('egress_lines.badge.stale_unknown');
  return t('egress_lines.badge.empty');
}

function badgeTone(badge: LineBadge): ChipTone {
  if (badge === 'excellent' || badge === 'same_region') return 'ok';
  if (badge === 'no_traffic' || badge === 'stale_unknown') return 'neutral';
  if (badge === 'good') return 'signal';
  if (badge === 'degraded') return 'warn';
  if (badge === 'breaker') return 'bad';
  return 'neutral';
}

function LinkRow({ link }: { readonly link: EgressLinkListEntry }): JSX.Element {
  const t = useT();
  const patch = usePatchEgressLinkStatus();
  const toast = useToast();
  const [confirm, setConfirm] = useState<EgressLinkStatus | null>(null);
  const nextStatus: EgressLinkStatus = link.stored_status === 'disabled' ? 'active' : 'disabled';

  async function apply(): Promise<void> {
    if (confirm === null) return;
    try {
      await patch.mutateAsync({ egressId: link.egress_id, status: confirm });
      toast.success(
        confirm === 'disabled'
          ? t('egress_lines.links.toast.disabled')
          : t('egress_lines.links.toast.enabled'),
      );
    } catch (err) {
      toast.error(err instanceof OpsApiError ? err.message : coerceErrorMessage(err));
    } finally {
      setConfirm(null);
    }
  }

  return (
    <Tr data-testid={`egress-lines-link-${link.egress_id}`}>
      <Td className="font-mono text-dense">{link.egress_id}</Td>
      <Td className="font-mono text-dense">{link.region}</Td>
      <Td className="font-mono text-micro">{link.endpoint}</Td>
      <Td>
        <Chip tone={derivedTone(link.derived_status)} lamp={false}>
          {link.derived_status}
        </Chip>
      </Td>
      <Td>
        <DispatchCell link={link} />
      </Td>
      <Td className="font-mono text-micro text-paper-low">{link.last_heartbeat_at}</Td>
      <Td className="text-dense text-paper-mid">{link.note ?? '—'}</Td>
      <Td>
        <Switch
          checked={link.stored_status === 'active'}
          onCheckedChange={() => setConfirm(nextStatus)}
          label={t('egress_lines.links.toggle')}
          testId={`egress-lines-toggle-${link.egress_id}`}
        />
        <ConfirmDialog
          open={confirm !== null}
          onOpenChange={(open) => {
            if (!open) setConfirm(null);
          }}
          title={nextStatus === 'disabled'
            ? t('egress_lines.links.confirm.disable_title')
            : t('egress_lines.links.confirm.enable_title')}
          body={t('egress_lines.links.confirm.body', { id: link.egress_id })}
          confirmLabel={nextStatus === 'disabled'
            ? t('egress_lines.links.confirm.disable')
            : t('egress_lines.links.confirm.enable')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          tone={nextStatus === 'disabled' ? 'danger' : 'default'}
          loading={patch.isPending}
          onConfirm={() => {
            void apply();
          }}
          testId={`egress-lines-confirm-${link.egress_id}`}
        />
      </Td>
    </Tr>
  );
}

function DispatchCell({ link }: { readonly link: EgressLinkListEntry }): JSX.Element {
  const t = useT();
  const patch = usePatchEgressLinkDispatch();
  const toast = useToast();
  const [confirmAdmit, setConfirmAdmit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const admitted = link.dispatch_enabled;

  async function apply(dispatchEnabled: boolean): Promise<void> {
    setError(null);
    try {
      await patch.mutateAsync({ egressId: link.egress_id, dispatchEnabled });
      toast.success(
        dispatchEnabled
          ? t('egress_lines.links.dispatch.toast.enabled')
          : t('egress_lines.links.dispatch.toast.disabled'),
      );
    } catch (err) {
      const message = err instanceof OpsApiError ? err.message : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setConfirmAdmit(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2 rounded bg-well px-2.5 py-2">
      <Chip
        tone={admitted ? 'ok' : 'warn'}
        testId={`egress-lines-dispatch-chip-${link.egress_id}`}
      >
        {admitted
          ? t('egress_lines.links.dispatch.admitted')
          : t('egress_lines.links.dispatch.standby')}
      </Chip>
      <Button
        size="sm"
        variant={admitted ? 'ghost' : 'secondary'}
        loading={patch.isPending}
        onClick={() => {
          if (admitted) {
            void apply(false);
            return;
          }
          setConfirmAdmit(true);
        }}
        data-testid={`egress-lines-dispatch-toggle-${link.egress_id}`}
      >
        {admitted
          ? t('egress_lines.links.dispatch.revoke')
          : t('egress_lines.links.dispatch.admit')}
      </Button>
      {error !== null ? (
        <p
          className="m-0 text-micro text-bad"
          role="alert"
          data-testid={`egress-lines-dispatch-error-${link.egress_id}`}
        >
          {error}
        </p>
      ) : null}
      <ConfirmDialog
        open={confirmAdmit}
        onOpenChange={(open) => {
          if (!open) setConfirmAdmit(false);
        }}
        title={t('egress_lines.links.dispatch.confirm.title')}
        body={t('egress_lines.links.dispatch.confirm.body', { id: link.egress_id })}
        confirmLabel={t('egress_lines.links.dispatch.confirm.admit')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={patch.isPending}
        onConfirm={() => {
          void apply(true);
        }}
        testId={`egress-lines-dispatch-confirm-${link.egress_id}`}
      />
    </div>
  );
}

function derivedTone(status: EgressLinkListEntry['derived_status']): ChipTone {
  if (status === 'active') return 'ok';
  if (status === 'degraded') return 'warn';
  return 'bad';
}

function TrafficCell({ requests, tokens }: { readonly requests: number; readonly tokens: number }): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-paper">{t('egress_lines.matrix.requests', { count: formatCount(requests) })}</span>
      <span className="text-paper-low">{t('egress_lines.matrix.tokens', { count: formatCount(tokens) })}</span>
    </div>
  );
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function ReplicationRow({ sample }: { readonly sample: ReplicationHealthSample }): JSX.Element {
  const t = useT();
  const status = presentedReplicationStatus(sample, Date.now());
  const delay = formatReplicationAge(sample.receipt_age_seconds);
  const missing = sample.publication_missing ?? [];
  const extra = sample.publication_extra ?? [];
  const drift = [
    ...missing.map((name) => `-${name}`),
    ...extra.map((name) => `+${name}`),
  ].join(' ');
  const sampled = relativeSample(sample.sampled_at, Date.now());
  const directionKey = sample.link_name === 'downlink'
    ? 'egress_lines.replication.downlink.direction'
    : 'egress_lines.replication.uplink.direction';
  return (
    <Tr data-testid={`egress-lines-replication-${sample.link_name}`}>
      <Td className="font-mono text-dense">{sample.link_name}</Td>
      <Td className="text-dense text-paper-mid">{t(directionKey)}</Td>
      <Td>
        <Chip tone={replicationTone(status)} lamp={false}>
          {replicationStatusLabel(t, status)}
        </Chip>
      </Td>
      <Td className="font-mono text-dense">
        <div className="flex flex-col gap-0.5">
          <span>{formatReplicationBytes(sample.lag_bytes)}</span>
          <span className="text-micro text-paper-low">
            {t('egress_lines.replication.lag_thresholds', {
              warn: formatReplicationBytes(REPLICATION_LAG_WARN_BYTES_DEFAULT),
              crit: formatReplicationBytes(REPLICATION_LAG_CRIT_BYTES_DEFAULT),
            })}
          </span>
        </div>
      </Td>
      <Td className="font-mono text-dense">{delay}</Td>
      <Td className="font-mono text-micro text-paper-low">{drift.length > 0 ? drift : '—'}</Td>
      <Td
        className={sampled?.stale === true ? 'font-mono text-micro text-warn' : 'font-mono text-micro text-paper-low'}
        data-testid={`egress-lines-replication-sampled-${sample.link_name}`}
      >
        {sampled === null
          ? t('egress_lines.replication.sampled_unknown')
          : t('egress_lines.replication.sampled_ago', { seconds: String(sampled.seconds) })}
      </Td>
    </Tr>
  );
}

function relativeSample(
  sampledAt: string,
  nowMs: number,
): { readonly seconds: number; readonly stale: boolean } | null {
  const then = Date.parse(sampledAt);
  if (!Number.isFinite(then)) return null;
  const ageMs = nowMs - then;
  return {
    seconds: Math.max(0, Math.floor(ageMs / 1000)),
    stale: ageMs > REPLICATION_STALE_AFTER_MS,
  };
}

function presentedReplicationStatus(
  sample: ReplicationHealthSample,
  nowMs: number,
): ReplicationHealthStatus {
  if (sample.status === 'unknown') return 'unknown';
  if (
    sample.status === 'absent'
    || sample.status === 'ok'
    || sample.status === 'warn'
    || sample.status === 'crit'
  ) {
    const stored: ReplicationStoredHealthStatus = sample.status;
    return presentReplicationHealthStatus({
      stored,
      sampledAt: sample.sampled_at,
      nowMs,
      linkName: sample.link_name,
      receiptAgeSeconds: sample.receipt_age_seconds,
    });
  }
  return 'unknown';
}

function replicationStatusLabel(
  t: (key: I18nKey) => string,
  status: ReplicationHealthStatus,
): string {
  if (status === 'ok') return t('egress_lines.replication.status.ok');
  if (status === 'warn') return t('egress_lines.replication.status.warn');
  if (status === 'crit') return t('egress_lines.replication.status.crit');
  if (status === 'unknown') return t('egress_lines.replication.status.unknown');
  return t('egress_lines.replication.status.absent');
}

function replicationTone(status: ReplicationHealthStatus): ChipTone {
  if (status === 'ok') return 'ok';
  if (status === 'warn') return 'warn';
  if (status === 'crit') return 'bad';
  if (status === 'unknown') return 'neutral';
  return 'neutral';
}


